import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  TextField,
  MenuItem,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  useTheme,
  useMediaQuery,
  Collapse,
  IconButton,
  Stack,
  Avatar,
  Divider,
  Fade,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth, db, storage, analytics } from "../firebase";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { convertPdfToImage } from "../utils/pdfToImage";
import SettingsIcon from "@mui/icons-material/Settings";
import { encryptField } from "../utils/encryption";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Lottie from "lottie-react";
import animationData from "../assets/welcome-animation.json";
import checkmarkAnimation from "../assets/success-checkmark.json";
import { logEvent } from "firebase/analytics";
import api from "../services/api";

const steps = [
  "Accountant Email",
  "Delivery Preferences",
  "Upload First Receipt",
];
const USER_AVATAR = "https://randomuser.me/api/portraits/women/44.jpg"; // swap for founder or actual testimonial!

function ReceiptPreview({ file }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => setSrc(e.target.result);
    reader.readAsDataURL(file);
  }, [file]);
  if (!file || !file.type.startsWith("image/")) return null;
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mt: 1,
        mb: 1.5,
        width: "100%",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={file.name}
        sx={{
          maxHeight: 80,
          maxWidth: "80%",
          borderRadius: 2,
          border: "1px solid #e0e7ef",
          boxShadow: "0 2px 8px #dbeafe33",
        }}
      />
    </Box>
  );
}

export default function Onboarding() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("manual");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState("free");
  const [completed, setCompleted] = useState(false);

  const isPro = plan === "pro" || plan === "proplus";

  useEffect(() => {
    const fetchUserStatus = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        setPlan(userDoc.data()?.plan || "free");
      }
    };
    fetchUserStatus();
  }, []);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleNext = async () => {
    if (uploading) return;
    setError("");
    if (activeStep === 0 && email.trim() && !isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (activeStep === 2 && file && file.size > 5 * 1024 * 1024) {
      setError("File too large. Max 5MB.");
      return;
    }
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setError("User not authenticated.");
      return;
    }
    setUploading(true);
    try {
      let fileUrl = null;
      let ocrResult = {};
      if (file) {
        const storageRef = ref(storage, `receipts/${uid}/${file.name}`);
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
        const formData = new FormData();
        let blobForOCR = file;
        if (file.type === "application/pdf") {
          try {
            blobForOCR = await convertPdfToImage(file);
          } catch (conversionError) {
            setError("Failed to process PDF receipt. Please try another file.");
            setUploading(false);
            return;
          }
        }
        formData.append("file", blobForOCR);
        try {
          const { data } = await api.post("/ocr/parse-receipt", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          ocrResult = data;
        } catch (err) {
          // Allow to continue even if OCR fails (user can edit later)
        }
        const now = new Date();
        await addDoc(collection(db, "receipts"), {
          uid,
          uploadedAt: Timestamp.fromDate(now),
          fileName: file.name,
          imageUrl: fileUrl,
          date: now.toISOString(),
          receiptMonth: now.getMonth(),
          receiptYear: now.getFullYear(),
          invoiceTotal: ocrResult.invoiceTotal ?? null,
          vatAmount: ocrResult.vatAmount ?? null,
          subtotal: ocrResult.subtotal ?? null,
          currency: ocrResult.currency ?? "€",
          amount: ocrResult.invoiceTotal ?? null,
        });
      }
      await setDoc(
        doc(db, "users", uid),
        {
          preferences: {
            accountantEmail: email.trim() ? encryptField(email.trim()) : null,
            deliveryMethod: method,
          },
          onboardingComplete: true,
        },
        { merge: true }
      );
      logEvent(analytics, "onboarding_complete", {
        uploaded_receipt: !!file,
        delivery_method: method,
        accountant_email_set: !!email.trim(),
      });
      setCompleted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(120deg, #f9fafc 70%, #f1f5f9 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 3 : 5,
          width: "100%",
          maxWidth: 500,
          borderRadius: 4,
          textAlign: "center",
          boxShadow: "0 6px 48px 0 rgba(30,64,175,0.13)",
          position: "relative",
          overflow: "visible",
        }}
      >
        {/* Intro Screen */}
        {showIntro ? (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                top: -60,
                left: "50%",
                transform: "translateX(-50%)",
                width: isMobile ? 140 : 170,
                height: isMobile ? 140 : 170,
                zIndex: 0,
                opacity: 0.93,
                filter: "drop-shadow(0 8px 32px #dbeafe88)",
              }}
            >
              <Lottie animationData={animationData} loop autoplay />
            </Box>
            <Box sx={{ height: isMobile ? 100 : 120 }} />
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={800}
              sx={{
                mb: 1.5,
                letterSpacing: -1,
                zIndex: 1,
                position: "relative",
                fontFamily: "Poppins, Inter, sans-serif",
              }}
            >
              Welcome to <span style={{ color: "#2563eb" }}>SortlyBooks</span>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 2.2,
                zIndex: 1,
                position: "relative",
                fontSize: isMobile ? "1rem" : "1.08rem",
              }}
            >
              The fastest way to organize receipts, track expenses, and send
              accountant-ready reports.
            </Typography>
            <Divider sx={{ mb: 2.5 }} />
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              <Avatar
                src={USER_AVATAR}
                alt="Emma"
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#e0e7ef",
                  boxShadow: "0 2px 8px #dbeafe44",
                  border: "2px solid #fff",
                }}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    fontSize: "0.97rem",
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  “SortlyBooks saves me hours every month — love it!”
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    fontSize: "0.87rem",
                  }}
                >
                  – Emma, Freelance Designer
                </Typography>
              </Box>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: "#16a34a",
                fontWeight: 600,
                letterSpacing: 0.1,
              }}
            >
              No spreadsheets. No stress. Just sorted.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{
                fontWeight: 700,
                letterSpacing: 0.3,
                py: 1.3,
                fontSize: "1.08rem",
                background: "linear-gradient(90deg, #2563eb 60%, #1e40af 100%)",
                borderRadius: 2,
                boxShadow: "0 4px 24px 0 rgba(30,64,175,0.10)",
                mt: 2,
                mb: 1,
                transition: "background 0.2s",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #1e40af 70%, #2563eb 100%)",
                },
              }}
              onClick={() => {
                logEvent(analytics, "onboarding_intro_start");
                setShowIntro(false);
              }}
            >
              Start Organizing My Receipts →
            </Button>
            <Typography
              variant="caption"
              sx={{
                mt: 2,
                color: "text.secondary",
                opacity: 0.8,
                fontSize: "0.93rem",
                display: "block",
              }}
            >
              Built for freelancers and small businesses. Your admin, your way.
            </Typography>
          </Box>
        ) : completed ? (
          // Completion Step
          <Fade in timeout={600}>
            <Box>
              <Lottie
                animationData={checkmarkAnimation}
                loop={false}
                autoplay
                style={{ height: 120, marginBottom: 16 }}
              />
              <Typography variant="h4" fontWeight={700} gutterBottom>
                🎉 You’re In!
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mb: 3, mt: 1 }}
              >
                You’re ready to take control of your business admin.
                <br />
                <span style={{ color: "#2563eb", fontWeight: 600 }}>
                  Pro tip:
                </span>{" "}
                Start uploading receipts as you go—SortlyBooks will handle the
                rest.
              </Typography>
              <Button
                startIcon={<CheckCircleIcon />}
                variant="contained"
                fullWidth
                size="large"
                sx={{ mb: 2 }}
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </Button>
              <Button
                startIcon={<SettingsIcon />}
                variant="text"
                fullWidth
                onClick={() => navigate("/dashboard/settings")}
              >
                Customize Settings
              </Button>
              <Typography
                variant="caption"
                sx={{ mt: 2, opacity: 0.85 }}
                color="text.secondary"
              >
                Need to invite your accountant or change settings?
                <span style={{ color: "#2563eb", fontWeight: 600 }}>
                  {" "}
                  You’ll find everything in Settings.
                </span>
              </Typography>
            </Box>
          </Fade>
        ) : (
          <>
            {/* Stepper with subtle background */}
            <Box
              sx={{
                mb: 4,
                p: 2,
                borderRadius: 2,
                background: "linear-gradient(90deg, #f1f5f9 75%, #e0e7ef 100%)",
                boxShadow: "0 2px 8px #dbeafe22",
              }}
            >
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, i) => (
                  <Step key={label} completed={activeStep > i}>
                    <StepLabel
                      sx={{
                        ".MuiStepLabel-label": {
                          fontWeight: activeStep === i ? 700 : 500,
                          color:
                            activeStep === i
                              ? "primary.main"
                              : "text.secondary",
                          letterSpacing: activeStep === i ? 0.2 : 0,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            {/* Step 1: Accountant Email */}
            {activeStep === 0 && (
              <Box sx={{ textAlign: "left" }}>
                <Card
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    background: "#f8fafc",
                  }}
                >
                  <Typography fontWeight={600}>
                    Set your accountant's email now, or skip and add later.
                  </Typography>
                </Card>
                <TextField
                  fullWidth
                  placeholder="e.g. accountant@firm.com"
                  label="Accountant's Email (optional)"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    sx: {
                      backgroundColor: "#fff",
                      borderRadius: 1,
                    },
                  }}
                  sx={{ mb: 1 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  If you add their email, reports are delivered automatically if
                  you upgrade to Pro.
                </Typography>
                <Chip
                  label="You can skip this step and add it later in Settings."
                  size="small"
                  sx={{
                    bgcolor: "#e0e7ef",
                    color: "#334155",
                    fontWeight: 500,
                    mb: 2,
                  }}
                />
                {error && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                )}
              </Box>
            )}
            {/* Step 2: Delivery Method */}
            {activeStep === 1 && (
              <Box sx={{ textAlign: "left" }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  justifyContent="center"
                  sx={{ mb: 2, flexDirection: isMobile ? "column" : "row" }}
                >
                  <Card
                    elevation={method === "manual" ? 4 : 0}
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 2,
                      border:
                        method === "manual"
                          ? "2px solid #2563eb"
                          : "1px solid #e0e7ef",
                      cursor: "pointer",
                      mb: isMobile ? 2 : 0,
                      background:
                        method === "manual"
                          ? "linear-gradient(90deg,#e0e7ef 70%,#f8fafc 100%)"
                          : "#f8fafc",
                    }}
                    onClick={() => setMethod("manual")}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      Manual (Free)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download your monthly report and send it yourself.
                    </Typography>
                  </Card>
                  <Card
                    elevation={method === "auto" ? 4 : 0}
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRadius: 2,
                      border:
                        method === "auto"
                          ? "2px solid #16a34a"
                          : "1px solid #e0e7ef",
                      cursor: isPro ? "pointer" : "not-allowed",
                      opacity: isPro ? 1 : 0.65,
                      background:
                        method === "auto"
                          ? "linear-gradient(90deg,#d1fae5 70%,#f8fafc 100%)"
                          : "#f8fafc",
                      pointerEvents: isPro ? "auto" : "none",
                    }}
                    onClick={() => isPro && setMethod("auto")}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color={isPro ? "success.main" : "text.secondary"}
                    >
                      Auto-Send (Pro)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email your monthly report automatically to your
                      accountant.
                    </Typography>
                    {!isPro && (
                      <Chip
                        label="Pro only"
                        size="small"
                        color="success"
                        sx={{ mt: 1, bgcolor: "#bbf7d0", color: "#15803d" }}
                      />
                    )}
                  </Card>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  You can always change your delivery preference from your
                  dashboard later.
                </Typography>
              </Box>
            )}
            {/* Step 3: Upload Receipt */}
            {activeStep === 2 && (
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ mb: 1, color: "#2563eb" }}
                >
                  Upload Your First Receipt
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Upload a receipt to get started (PDF or image). We’ll scan the
                  total for you!
                </Typography>
                <Button
                  variant={file ? "outlined" : "contained"}
                  component="label"
                  fullWidth
                  sx={{ mt: 1.5, mb: 2, py: 1.5 }}
                >
                  {file ? "Replace Receipt" : "Upload Receipt (PDF or Image)"}
                  <input
                    type="file"
                    hidden
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      if (e.target.files?.[0]) setFile(e.target.files[0]);
                    }}
                  />
                </Button>
                <Collapse in={!!file}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "#f1f5f9",
                      p: 1.5,
                      borderRadius: 2,
                      mt: -1.5,
                      mb: 1,
                    }}
                  >
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      <strong>{file?.name}</strong> added
                    </Typography>
                    <IconButton
                      onClick={() => setFile(null)}
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <ReceiptPreview file={file} />
                </Collapse>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
                  color="text.secondary"
                >
                  You can add more receipts later from your dashboard—this just
                  gets you started!
                </Typography>
                {error && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                )}
              </Box>
            )}
            {/* Navigation */}
            <Box
              sx={{
                mt: 4,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 2,
              }}
            >
              <Button
                fullWidth
                onClick={handleBack}
                disabled={activeStep === 0 || uploading}
                sx={{
                  background: "#e0e7ef",
                  fontWeight: 600,
                  color: "#2563eb",
                  "&:hover": { background: "#dbeafe" },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleNext}
                disabled={uploading}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  background:
                    "linear-gradient(90deg, #2563eb 60%, #1e40af 100%)",
                  borderRadius: 2,
                  boxShadow: "0 4px 24px 0 rgba(30,64,175,0.09)",
                  py: 1.3,
                  fontSize: "1.07rem",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #1e40af 70%, #2563eb 100%)",
                  },
                }}
              >
                {uploading ? (
                  <CircularProgress size={20} />
                ) : activeStep === steps.length - 1 ? (
                  "Finish"
                ) : (
                  "Next"
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
